import { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import Alert from "@mui/material/Alert";
import AppBar from "@mui/material/AppBar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Container from "@mui/material/Container";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import { usePortalAuth } from "./PortalAuthProvider";
import { PortalPasswordDialog } from "./PortalPasswordDialog";

/**
 * The portal shell.
 *
 * Two screens and an account menu — deliberately not the console's drawer. A
 * subscriber opens this to answer "what came in, and did I call it back"; a
 * navigation tree implies there is more here, and there is not.
 */

const NAV = [
  { to: "/portal", label: "Overview", end: true },
  { to: "/portal/leads", label: "Leads", end: false },
];

export function PortalLayout() {
  const { user, signOut } = usePortalAuth();
  const navigate = useNavigate();
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [passwordOpen, setPasswordOpen] = useState(false);

  const handleSignOut = async () => {
    setMenuAnchor(null);
    await signOut();
    navigate("/portal/login", { replace: true });
  };

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default" }}>
      <AppBar
        position="sticky"
        color="default"
        elevation={0}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Toolbar variant="dense" sx={{ gap: 2 }}>
          <Typography variant="h6" sx={{ flexShrink: 0 }}>
            {user?.client.name ?? "FieldClients"}
          </Typography>
          <Stack direction="row" spacing={0.5} sx={{ flexGrow: 1 }}>
            {NAV.map((entry) => (
              <Button
                key={entry.to}
                component={NavLink}
                to={entry.to}
                end={entry.end}
                color="inherit"
                sx={{ "&.active": { color: "primary.main", fontWeight: 600 } }}
              >
                {entry.label}
              </Button>
            ))}
          </Stack>
          {user && user.client.status !== "active" ? (
            <Chip label={user.client.status} color="warning" variant="outlined" />
          ) : null}
          <Button
            startIcon={<AccountCircleIcon />}
            onClick={(e) => setMenuAnchor(e.currentTarget)}
            color="inherit"
          >
            {user?.email ?? "…"}
          </Button>
          <Menu anchorEl={menuAnchor} open={menuAnchor !== null} onClose={() => setMenuAnchor(null)}>
            <MenuItem
              onClick={() => {
                setMenuAnchor(null);
                setPasswordOpen(true);
              }}
            >
              Change password
            </MenuItem>
            <MenuItem onClick={handleSignOut}>Sign out</MenuItem>
          </Menu>
        </Toolbar>
      </AppBar>

      <Container maxWidth="xl" sx={{ py: 3 }}>
        {/* A paused client keeps its portal — the operator stopped routing new
            leads, and the ones already delivered still need chasing and reporting. */}
        {user?.client.status === "paused" ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            New leads are paused for this account. Everything already delivered is still here, and
            outcomes can still be reported.
          </Alert>
        ) : null}
        <Outlet />
      </Container>

      <PortalPasswordDialog open={passwordOpen} onClose={() => setPasswordOpen(false)} />
    </Box>
  );
}
